import React, { useCallback, useEffect, useMemo, useState } from "react";
import api from "utils/api";
import { adminBrandParams } from "utils/brand";
import { useTeamAdminBrand } from "./TeamAdminBrandContext";
import { useTeamLocale } from "./TeamLocaleContext";
import { formatEuro, formatRevenueEntryDate } from "./formatters";

const todayIso = () => {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

const startOfMonthIso = () => {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  return `${y}-${m}-01`;
};

const ComparisonTab = () => {
  const { t, dateLocale, locale } = useTeamLocale();
  const adminBrand = useTeamAdminBrand();
  const [from, setFrom] = useState(startOfMonthIso);
  const [to, setTo] = useState(todayIso);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!from) {
      setError(t("comparison.dateRequired"));
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const { data: res } = await api.get("/admin/revenue-comparison", {
        params: {
          ...adminBrandParams({ brand: adminBrand }),
          from,
          to: to || from,
        },
      });
      setData(res);
    } catch (err) {
      setData(null);
      setError(err?.response?.data?.message || t("comparison.loadError"));
    } finally {
      setLoading(false);
    }
  }, [from, to, adminBrand, t]);

  useEffect(() => {
    load();
  }, [load]);

  const periodLabel = useMemo(() => {
    if (!data?.from) return "";
    if (data.from === data.to) {
      return formatRevenueEntryDate(data.from, dateLocale);
    }
    return t("comparison.periodRange", {
      from: formatRevenueEntryDate(data.from, dateLocale),
      to: formatRevenueEntryDate(data.to, dateLocale),
    });
  }, [data, dateLocale, t]);

  return (
    <div className="team-comparison">
      <p className="team-lead">{t("comparison.lead")}</p>

      <div className="team-comparison-form">
        <div className="team-comparison-dates">
          <label htmlFor="cmp-from">
            {t("comparison.from")}
            <input
              id="cmp-from"
              type="date"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
              required
            />
          </label>
          <label htmlFor="cmp-to">
            {t("comparison.to")}
            <input
              id="cmp-to"
              type="date"
              value={to}
              min={from || undefined}
              onChange={(e) => setTo(e.target.value)}
            />
          </label>
          {loading && (
            <span className="team-muted team-comparison-loading">{t("comparison.loading")}</span>
          )}
        </div>
        <p className="field-note">{t("comparison.dateHint")}</p>
      </div>

      {error && <div className="auth-alert is-error">{error}</div>}

      {loading && !data && (
        <div className="team-empty">{t("common.loading")}</div>
      )}

      {data && (
        <>
          <p className="team-comparison-period">{periodLabel}</p>

          <div className="team-stats-grid team-comparison-stats">
            <div className="team-stat">
              <span className="team-stat-label">{t("comparison.totalRevenue")}</span>
              <span className="team-stat-value">
                {formatEuro(data.totalRevenueCents, locale)}
              </span>
              <span className="team-stat-hint">
                {t("comparison.dayCount", { count: data.dayCount })}
              </span>
            </div>
            <div className="team-stat">
              <span className="team-stat-label">{t("comparison.bookingRevenue")}</span>
              <span className="team-stat-value">
                {formatEuro(data.bookingRevenueCents, locale)}
              </span>
              <span className="team-stat-hint">
                {t("comparison.bookingCount", { count: data.bookingCount })}
              </span>
            </div>
            <div className="team-stat">
              <span className="team-stat-label">{t("comparison.manualRevenue")}</span>
              <span className="team-stat-value">
                {formatEuro(data.manualRevenueCents, locale)}
              </span>
              <span className="team-stat-hint">
                {t("comparison.manualCount", { count: data.manualEntryCount })}
              </span>
            </div>
          </div>

          <div className="team-table-wrap">
            <table className="team-table team-comparison-table">
              <thead>
                <tr>
                  <th>{t("comparison.colDate")}</th>
                  <th>{t("comparison.colBookings")}</th>
                  <th>{t("comparison.colManual")}</th>
                  <th>{t("comparison.colTotal")}</th>
                </tr>
              </thead>
              <tbody>
                {data.days.map((day) => (
                  <tr
                    key={day.date}
                    className={day.totalRevenueCents === 0 ? "is-muted" : ""}
                  >
                    <td>{formatRevenueEntryDate(day.date, dateLocale)}</td>
                    <td>
                      {formatEuro(day.bookingRevenueCents, locale)}
                      {day.bookingCount > 0 && (
                        <span className="team-muted team-comparison-count">
                          {" "}
                          ({day.bookingCount})
                        </span>
                      )}
                    </td>
                    <td>
                      {formatEuro(day.manualRevenueCents, locale)}
                      {day.manualEntryCount > 0 && (
                        <span className="team-muted team-comparison-count">
                          {" "}
                          ({day.manualEntryCount})
                        </span>
                      )}
                    </td>
                    <td>
                      <strong>{formatEuro(day.totalRevenueCents, locale)}</strong>
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td>{t("comparison.totalRow")}</td>
                  <td>{formatEuro(data.bookingRevenueCents, locale)}</td>
                  <td>{formatEuro(data.manualRevenueCents, locale)}</td>
                  <td>
                    <strong>{formatEuro(data.totalRevenueCents, locale)}</strong>
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </>
      )}
    </div>
  );
};

export default ComparisonTab;
